import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Client } from "@xmpp/client"
import {
  fetchSearchHistoryPage,
  fetchSearchHistoryAroundId,
  HistoryDirection,
} from "@/api/searchHistory"
import { SearchHit } from "@/lib/searchResultParser"
import messagesReducer, {
  buildMessages,
  MessagesState,
  processArchivedBatch,
} from "@/reducers/messagesSlice"
import {
  resolveSearchHistoryAnchor,
  searchHistoryMessages,
} from "@/lib/searchHistoryMessages"
import i18n from "@/lib/i18n/i18n"

type Boundary = { cursor?: string; hasMore: boolean; error?: string }
type HistoryState = {
  anchorMessageId?: string
  messages: MessagesState
  older: Boundary
  newer: Boundary
  loading: HistoryDirection | null
  error?: string
  errorKind?: "permission"
  shift: boolean
}

const MAX_CORRECTION_LOOKUPS = 4

export default function useSearchHistory(client: Client, hit: SearchHit) {
  const { chat_jid: chatJid, id: messageId, timestamp } = hit.document
  const [state, setState] = useState<HistoryState>()
  const generation = useRef(0)
  const busy = useRef(false)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    const currentGeneration = ++generation.current
    busy.current = true
    setState(undefined)
    const load = async () => {
      try {
        let [older, newer] = await Promise.all([
          fetchSearchHistoryPage({
            client,
            chatJid,
            direction: "older",
            timestamp,
          }),
          fetchSearchHistoryPage({
            client,
            chatJid,
            direction: "newer",
            timestamp,
          }),
        ])
        if (generation.current !== currentGeneration) return
        const knownPages = [older, newer]
        const buildWindow = () => {
          let window: MessagesState = { [chatJid]: buildMessages({}) }
          for (const page of [older, newer])
            window = messagesReducer(window, processArchivedBatch(page.payload))
          // Carry edits and metadata forward, but never merge disjoint message
          // windows: that would leave a silent gap when scrolling.
          for (const page of knownPages) {
            if (page === older || page === newer) continue
            window = messagesReducer(
              window,
              processArchivedBatch({ ...page.payload, messages: [] }),
            )
          }
          return window
        }
        let messages = buildWindow()
        let anchor = resolveSearchHistoryAnchor(messages[chatJid], {
          id: messageId,
          timestamp,
        })
        const lookedUp = new Set<string>()
        while (anchor?.missingOriginalId) {
          if (
            lookedUp.has(anchor.missingOriginalId) ||
            lookedUp.size >= MAX_CORRECTION_LOOKUPS
          )
            throw new Error("Could not resolve archive correction reference")
          lookedUp.add(anchor.missingOriginalId)
          const window = await fetchSearchHistoryAroundId({
            client,
            chatJid,
            messageId: anchor.missingOriginalId,
          })
          if (generation.current !== currentGeneration) return
          older = window.older
          newer = window.newer
          knownPages.push(older, newer)
          messages = buildWindow()
          anchor = resolveSearchHistoryAnchor(messages[chatJid], {
            id: messageId,
            timestamp,
          })
        }
        if (!anchor?.messageId)
          throw new Error(
            "Could not identify the selected message in the archive window",
          )
        setState({
          anchorMessageId: anchor.messageId,
          messages,
          older: { cursor: older.cursor, hasMore: older.hasMore },
          newer: { cursor: newer.cursor, hasMore: newer.hasMore },
          loading: null,
          shift: false,
        })
      } catch (error) {
        if (generation.current !== currentGeneration) return
        console.error("Failed to open search history", error)
        const denied =
          typeof error === "object" &&
          error !== null &&
          "condition" in error &&
          error.condition === "forbidden"
        setState({
          messages: { [chatJid]: buildMessages({}) },
          older: { hasMore: false },
          newer: { hasMore: false },
          loading: null,
          shift: false,
          error: denied
            ? i18n.t("app.search.errors.noAccess")
            : i18n.t("app.search.errors.openFailed"),
          errorKind: denied ? "permission" : undefined,
        })
      } finally {
        if (generation.current === currentGeneration) busy.current = false
      }
    }
    void load()
    return () => {
      generation.current = currentGeneration + 1
    }
  }, [client, chatJid, messageId, timestamp, retry])

  const loadMore = useCallback(
    async (direction: HistoryDirection) => {
      if (!state || state.error || busy.current || !state[direction].hasMore)
        return
      const currentGeneration = generation.current
      busy.current = true
      setState(
        (previous) =>
          previous && {
            ...previous,
            loading: direction,
            [direction]: { ...previous[direction], error: undefined },
          },
      )
      try {
        const page = await fetchSearchHistoryPage({
          client,
          chatJid,
          direction,
          cursor: state[direction].cursor,
        })
        if (generation.current !== currentGeneration) return
        setState(
          (previous) =>
            previous && {
              ...previous,
              messages: messagesReducer(
                previous.messages,
                processArchivedBatch(page.payload),
              ),
              [direction]: { cursor: page.cursor, hasMore: page.hasMore },
              loading: null,
              shift: direction === "older",
            },
        )
      } catch (error) {
        if (generation.current !== currentGeneration) return
        console.error("Failed to load search history", error)
        setState(
          (previous) =>
            previous && {
              ...previous,
              loading: null,
              [direction]: {
                ...previous[direction],
                error: i18n.t("app.search.errors.loadFailed"),
              },
            },
        )
      } finally {
        if (generation.current === currentGeneration) busy.current = false
      }
    },
    [client, chatJid, state],
  )

  const messages = useMemo(() => {
    const chat = state?.messages[chatJid]
    return chat ? searchHistoryMessages(chat) : []
  }, [chatJid, state?.messages])

  return {
    state,
    messages,
    loadMore,
    retry: () => setRetry((value) => value + 1),
  }
}
